/**
 * Public warranty registration — opened from the QR on the warranty certificate (Phase 2 plan 09, X13).
 * Shell copied from PaymentConfirmationForm (same public, no-AppShell layout and API_URL).
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, CheckCircle } from 'lucide-react';

const API_URL = ((import.meta as any).env?.VITE_API_URL as string | undefined) || '/api/v1';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const istDay = (iso: string) => {
  const d = new Date(new Date(iso).getTime() + 330 * 60 * 1000);
  return `${String(d.getUTCDate()).padStart(2, '0')} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

interface Info {
  productName: string;
  serial: string;
  startDate: string;
  endDate: string;
  registered: boolean;
}

export function WarrantyRegistrationPage() {
  const { token } = useParams<{ token: string }>();
  const [info, setInfo] = useState<Info | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/public/warranty-registration/${token}`);
        const body = await res.json();
        if (!res.ok) setError(body?.error ?? 'This warranty link is not valid.');
        else setInfo(body.data);
      } catch {
        setError('Could not reach the server. Please try again.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/public/warranty-registration/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), phone: phone.trim() }),
      });
      const body = await res.json();
      if (!res.ok) setError(body?.error ?? 'Registration failed. Please try again.');
      else setDone(true);
    } catch {
      setError('Could not reach the server. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#faf8f4] p-4">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#f5870a] text-white text-2xl font-bold shadow">A</div>
          <h1 className="font-display text-2xl font-bold text-gray-900">Register your warranty</h1>
          <p className="mt-1 text-sm text-gray-500">So we can reach you for service on this product.</p>
        </div>
        <div className="rounded-3xl bg-white p-6 shadow-lg space-y-5">
          {loading && <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>}
          {!loading && error && !info && <p className="text-sm text-red-600">{error}</p>}
          {info && (
            <>
              <div className="mb-4 space-y-1 text-sm">
                <p className="font-semibold text-gray-900">{info.productName}</p>
                <p className="font-mono text-xs text-gray-600">{info.serial}</p>
                <p className="text-gray-600">{`${istDay(info.startDate)} – ${istDay(info.endDate)}`}</p>
              </div>
              {done ? (
                <div className="text-center py-4">
                  <CheckCircle className="mx-auto mb-2 h-10 w-10 text-green-600" />
                  <h2 className="mb-2 font-display text-2xl font-bold text-gray-900">Warranty registered</h2>
                </div>
              ) : info.registered ? (
                <p className="text-sm text-gray-700">This warranty is already registered.</p>
              ) : (
                <form onSubmit={submit} className="space-y-3">
                  <label className="mb-1 block text-sm font-semibold text-gray-700">
                    Your name
                    <input aria-label="Your name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-[#f5870a] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#f5870a]/20" />
                  </label>
                  <label className="mb-1 block text-sm font-semibold text-gray-700">
                    Mobile number
                    <input aria-label="Mobile number" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1 w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-[#f5870a] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#f5870a]/20" />
                  </label>
                  {error && <p className="text-sm text-red-600">{error}</p>}
                  <button type="submit" disabled={busy} className="w-full rounded-xl bg-[#f5870a] px-4 py-3 text-sm font-bold text-white shadow hover:bg-[#e07809] disabled:opacity-60 transition-colors">
                    {busy ? 'Registering…' : 'Register warranty'}
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
